import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * The decisions inbox (everything waiting for the vendor's yes) and the
 * morning brief that carries it — pms-api-v2 `services/pms/decisions`
 * (feat/room-to-revenue, workstream D).
 */

export type DecisionSource = 'request' | 'lead' | 'assistant' | 'agent_run' | 'insight' | 'content_pack' | 'setup';
export type DecisionActionId = 'approve' | 'decline' | 'done' | 'snooze' | 'open';
export type Urgency = 'now' | 'today' | 'week';

export interface DecisionItem {
  id: string;
  source: DecisionSource;
  title: string;
  detail?: string;
  value?: { amount: number; currency: string };
  urgency: Urgency;
  createdAt: string;
  href: string;
  media?: string;
  actions: { id: DecisionActionId; kind: 'primary' | 'secondary' }[];
  kind?: string;
  i18n?: { title?: string; detail?: string; params?: Record<string, string | number> };
  meta?: Record<string, any>;
}

export interface DecisionCounts {
  total: number;
  now: number;
  today: number;
  week: number;
  bySource: Record<DecisionSource, number>;
}

export interface DecisionList {
  items: DecisionItem[];
  counts: DecisionCounts;
  generatedAt: string;
  sources: DecisionSource[];
}

export interface ActResult {
  ok: boolean;
  id: string;
  action: DecisionActionId;
  already?: boolean;
  error?: string;
  summary?: string;
  href?: string;
  until?: string;
}

export type BriefLanguage = 'auto' | 'en' | 'fr' | 'es' | 'ar';

export interface BriefDelivery {
  channelUuid: string;
  kind: string;
  name: string;
  sent: number;
  failed: number;
  note?: 'whatsapp_window' | 'no_chat' | 'no_outbound' | 'not_connected' | 'failed' | string;
  error?: string;
}

export interface BriefRunSummary {
  at: string;
  kind: 'scheduled' | 'test';
  status: 'sent' | 'partial' | 'failed' | 'skipped';
  headline: string;
  decisions: number;
  deliveries: BriefDelivery[];
  email: { sent: number; note?: string };
  bell: boolean;
}

export interface BriefSettings {
  enabled: boolean;
  hour: number;
  minute: number;
  timezone: string;
  weekdays: number[];
  channelUuids: string[];
  email: boolean;
  emailUserIds: string[];
  language: BriefLanguage;
  aiLine: boolean;
  skipWhenQuiet: boolean;
  nextRunAt: string | null;
  lastRun: BriefRunSummary | null;
  saved: boolean;
}

export type BriefSettingsInput = Partial<Pick<BriefSettings, 'enabled' | 'hour' | 'minute' | 'timezone' | 'weekdays' | 'channelUuids' | 'email' | 'language' | 'aiLine' | 'skipWhenQuiet'>>;

export interface BriefChatMessage {
  /** Light markdown: **bold** and [label](url). */
  text: string;
  /** The same in the app's own dialect. */
  formatted: string;
  buttons?: { id: string; label: string; style?: 'primary' | 'danger' }[][];
  media?: { url: string; kind: 'image' }[];
}

export interface BriefPreview {
  lang: 'en' | 'fr' | 'es' | 'ar';
  lineSource: 'model' | 'fallback' | 'cache' | 'off';
  brief: {
    title: string;
    headline: string;
    lines: string[];
    line: string | null;
    decisions: { id: string; title: string; detail?: string; value?: string; url: string; media?: string }[];
    more: number;
    picture: string | null;
    quiet: boolean;
  };
  channels: {
    whatsapp: BriefChatMessage[];
    telegram: BriefChatMessage[];
    slack: BriefChatMessage[];
    webhook: Record<string, any>;
  };
  email: { subject: string; html: string; text: string };
  targets: { uuid: string; kind: string; name: string; status: string; chats: number; outbound: boolean }[];
  settings: BriefSettings;
}

export const useDecisionsApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();
  const call = <T,>(method: ApiMethod, path: string, body?: any) => sendAuthGuardedRequest(method, path, body) as Promise<T>;
  const q = (lang?: string) => (lang ? `?lang=${encodeURIComponent(lang)}` : '');

  return {
    list: (lang?: string) => call<DecisionList>(ApiMethod.GET, `/services/pms/decisions${q(lang)}`),
    act: (id: string, action: DecisionActionId, opts: { until?: string; lang?: string } = {}) =>
      call<ActResult>(ApiMethod.POST, `/services/pms/decisions/${encodeURIComponent(id)}/${action}${q(opts.lang)}`, opts.until ? { until: opts.until } : {}),

    getBriefSettings: () => call<BriefSettings>(ApiMethod.GET, '/services/pms/decisions/brief/settings'),
    updateBriefSettings: (input: BriefSettingsInput) => call<BriefSettings>(ApiMethod.PUT, '/services/pms/decisions/brief/settings', input),
    previewBrief: (lang?: string) => call<BriefPreview>(ApiMethod.GET, `/services/pms/decisions/brief/preview${q(lang)}`),
    sendTestBrief: () => call<BriefRunSummary>(ApiMethod.POST, '/services/pms/decisions/brief/send-test'),
  };
};
